<template>
  <div v-if="filterByProductCount.length" :class="$style.category">
    <div v-for="(child, idx) in filterByProductCount" :key="child.id" :class="$style.categoryCell">
      <lv-link :class="$style.categoryCellLink" :to="getCategoryUrl(child)">
        <span :class="$style.categoryCellLinkText" v-text="getCategoryDisplayName(child)" />
        <div :class="$style.categoryCellLinkIllustration">
          <lv-image
            v-if="imageSources[idx]"
            :type="LV_IMG_TYPE_CATEGORY"
            :sources="imageSources[idx]"
            :class="$style.categoryCellLinkIllustrationImage"
          />
          <img
            v-else
            :src="$addWebpPostfixIfSupported({ url: abstract77, webpUrl: abstract77Webp })"
            alt=""
            loading="lazy"
            :class="$style.categoryCellLinkIllustrationFallback"
          >
        </div>
      </lv-link>
    </div>
  </div>
</template>

<script setup>
  import { LvLink, LvImage } from '~~/common/components/loginet-vue-shop/index';
  import { getCategoryUrl } from '~~/common/composables/useUrls';
  import { getCategoryDisplayName } from '~~/common/utils/category';
  import { webpFallback } from '~~/common/utils';
  import { LV_IMG_TYPE_CATEGORY } from '~~/common/config/LvImageType';
  import abstract77 from '~~/common/assets/images/abstract-77.png';
  import abstract77Webp from '~~/common/assets/images/abstract-77.png.webp';

  const props = defineProps({
    category: {
      type: Object,
      required: true,
    },
  });

  const filterByProductCount = computed(() => {
    return (props.category?.children || []).filter(child => child.productCount);
  });
  const imageSources = computed(() => {
    return filterByProductCount.value.map((child) => {
      return child?.thumbnailUrl ? webpFallback([{ url: child.thumbnailUrl }]) : null;
    });
  });
</script>

<style module lang="scss" rel="stylesheet/scss">
.category {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  @include tablet(min) {
    grid-template-columns: repeat(3, 1fr);
  }
  @include desktop-small(min) {
    grid-template-columns: repeat(4, 1fr);
  }
  @include desktop-medium(min) {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 16px;
  }

  &Cell {
    padding: 4px 4px 4px 12px;
    border-radius: 12px;
    background-color: $color-white;
    box-shadow: 0 2px 8px 0 rgba($color-olive-750, 0.12);
    @include tablet(min) {
      padding: 8px 8px 8px 16px;
      border-radius: 16px;
      box-shadow: 0 8px 24px 0 rgba($color-olive-750, 0.12);
    }

    &Link {
      display: grid;
      align-items: center;
      justify-content: space-between;
      height: 100%;
      grid-template-columns: auto 64px;
      gap: 8px;

      &Text {
        font-size: 12px;
        line-height: 16px;
        display: -webkit-box;
        overflow: hidden;
        max-height: 2*16px;
        text-overflow: ellipsis;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        @include tablet(min) {
          font-size: 14px;
          line-height: 20px;
          max-height: 2*20px;
        }
      }

      &Illustration {
        display: flex;
        overflow: hidden;
        align-items: center;
        justify-content: center;
        width: 48px;
        height: 48px;
        padding: 4px;
        border-radius: 8px;
        background-color: $color-background-2;
        justify-self: flex-end;
        @include tablet(min) {
          width: 64px;
          height: 64px;
          border-radius: 12px;
        }

        &Image {
          width: 100%;
          height: 100%;
        }

        &Fallback {
          width: 80%;
          height: 80%;
        }
      }
    }
  }
}
</style>
